import { Button, Input, message, Tooltip } from 'antd';
import { Form, Formik } from 'formik';
import { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';

import { trackButtonClick } from '../../analytics/mixpanel';
import { CollapseSidePanelIcon } from '../../assets';
import SearchOutlined from '../../assets/common/SearchOutlined';
import FileUploadOutlined from '../../assets/docchat/FileUploadOutlined';
import { acceptTypes } from '../../Constants';
import { DOCCHAT_MAX_FILES, ERROR, LOADING, SUCCESS } from '../../Constants/commonConstants';
import { useSelectedUploadData } from '../../Features/Chatbot/useSelectedUploadData';
import CreateFolderModal from '../../Features/FilePicker/CreateFolderModal';
import { TAKE_TOUR_TYPES } from '../../Features/TakeTour/constants/constants';
import { dummyData } from '../../Features/TakeTour/constants/dummyData';
import { useTakeTour } from '../../Features/TakeTour/hooks/useTakeTour';
import {
  deleteChat,
  deleteFile,
  getChatHistory,
  handleMoveToFolder,
  moveToFolder,
  removeFromFolder,
  renameFile,
} from '../../Redux/DocChat/docChatActions';
import ConfirmationModal from '../UI/ConfirmationModal';
import UpdateFileNameModal from '../UI/UpdateFileNameModal';
import SavedComparisonSkeleton from './SavedComparisonSkeleton';
import SavedComparisonsListItem from './SavedComparisonsListItem';

const initialModalData = {
  edit: false,
  delete: false,
  newFolder: false,
  data: null,
};

function SavedComparisonsDocChat({
  userData,
  docChatQuota,
  selectedDoc,
  selectedCollection,
  uploadStatus = '',
  screen,
}) {
  const dispatch = useDispatch();
  const { records, savedDocChatRecordStatus } = useSelector((state) => state.docChatData);
  const { tourType } = useTakeTour();
  const [sidePanelVisible, setSidePanelVisible] = useState(true);
  const [modalData, setModalData] = useState(initialModalData);
  const [loader, setLoader] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [filteredRecords, setFilteredRecords] = useState(records);
  const calculatedRecords = tourType === TAKE_TOUR_TYPES.DOCCHAT_CHAT_VIEWER ? dummyData : records;
  const calculatedFilteredRecords =
    tourType === TAKE_TOUR_TYPES.DOCCHAT_CHAT_VIEWER ? dummyData : filteredRecords;
  const [expandedCollections, setExpandedCollections] = useState(new Set());
  const navigate = useNavigate();
  const location = useLocation();
  const { pathname } = location;
  const fileRef = useRef();
  const addRef = useRef();
  const selectedCollectionRef = useRef(null);
  const sidePanelRef = useRef(null);
  const { uuid, position } = useSelectedUploadData();

  useEffect(() => {
    if (selectedCollection?.collection_id) {
      setExpandedCollections(new Set([selectedCollection?.collection_id]));
    }
  }, [selectedCollection?.collection_id]);

  useEffect(() => {
    if (selectedCollectionRef?.current && sidePanelRef.current) {
      // Scroll the side panel to the selected item
      const scrollToPosition = () => {
        if (!selectedCollectionRef?.current) return;
        const offsetTop = selectedCollectionRef?.current.offsetTop;
        const scrollPosition = offsetTop - 500; // Adjust this value if needed
        sidePanelRef.current.scrollTop = scrollPosition;
      };

      // Wait for the DOM to render before scrolling
      setTimeout(scrollToPosition, 100); // Delay in milliseconds (you can adjust this)
    }
  }, [calculatedRecords, selectedDoc, selectedCollection]);

  useEffect(() => {
    if (userData?.id && pathname !== '/viewer-docchat') {
      dispatch(getChatHistory({ userId: userData.id }));
    }
  }, [userData]);

  useEffect(() => {
    setFilteredRecords(calculatedRecords);
  }, [calculatedRecords]);

  const handleModal = (name, data) => {
    if (name === 'removeFromFolder') {
      removeFromFolder({
        collectionId: data?.collectionId,
        documentId: data?.document_id,
        navigate,
        pathname,
        userId: userData?.id,
        dispatch,
      });
      return;
    }
    if (name === 'moveToFolder') {
      moveToFolder({
        collectionId: data?.collectionId,
        documentId: data?.document_id,
        userId: userData?.id,
        dispatch,
        pathname,
        navigate,
      });
      return;
    }
    setModalData((prev) => ({
      ...prev,
      [name]: true,
      ...(data ? { data } : {}),
    }));
  };

  const closeModal = () => {
    setModalData({ ...initialModalData });
  };

  const handleUpload = ({ file, collection_id, noLeft }) => {
    // // Check for token
    // const token = localStorage.getItem('access_token');
    // if (!token) {
    //   navigate('/');
    //   return;
    // }
    navigate('/docchat', { state: { file, collection_id, noLeft } });
  };

  const handleSidePanel = () => {
    setSidePanelVisible(!sidePanelVisible);
  };

  const handleDocumentClick = ({ document_id, collection_id, index = null }) => {
    trackButtonClick({ type: 'Document Selected' });
    if (collection_id) {
      navigate({
        pathname: '/viewer-docchat',
        search: `?uuid=${collection_id}&type=SAVED&position=${index || 0}`, // Add position as a query parameter
      });
    } else {
      navigate({
        pathname: '/viewer-docchat',
        search: `?uuid=${collection_id || document_id}&type=SAVED`, // Add position as a query parameter
      });
    }
  };

  const handleEditDocName = async (newFileName) => {
    trackButtonClick({ type: 'Document Renamed' });
    setLoader('EDIT');
    if (savedDocChatRecordStatus === LOADING) {
      message.error('Document list is loading, please try again after few seconds');
      return;
    }
    const nameExists = calculatedRecords?.some((doc) => {
      return doc.document_name === newFileName || doc.collection_name === newFileName;
    });
    if (nameExists) {
      message.error('Name already exists');
      return;
    }
    try {
      const collectionId = modalData.data?.collection_id;
      const documentId = modalData.data?.document_id;
      await renameFile({
        documentId: collectionId ? null : documentId,
        newName: newFileName,
        oldName: modalData.data?.[collectionId ? 'collection_name' : 'document_name'],
        collectionId,
      });
      dispatch(getChatHistory({ userId: userData?.id }));
    } finally {
      setLoader(null);
      closeModal();
    }
  };

  const editValidation = (newFileName) => {
    const nameExists = calculatedRecords?.some((doc) => {
      return doc.document_name === newFileName || doc.collection_name === newFileName;
    });
    if (nameExists) {
      message.error('Name already exists');
      if (loader) setLoader(null);
      return false;
    }
    return true;
  };

  const handleDeleteDoc = async () => {
    setLoader('DELETE');
    trackButtonClick({ type: 'Document Deleted' });
    try {
      const collectionId = modalData.data?.collection_id;
      const documentId = modalData.data?.document_id;
      await deleteFile({
        collectionId,
        documentId,
        title: modalData.data?.[collectionId ? 'collection_name' : 'document_name'],
      });
      dispatch(getChatHistory({ userId: userData?.id }));
      if (
        pathname === '/viewer-docchat' &&
        (selectedCollection?.collection_id === collectionId ||
          selectedDoc?.document_id === documentId)
      ) {
        navigate('/docchat');
      }
    } finally {
      setLoader(null);
      closeModal();
    }
  };

  const handleSearch = (event) => {
    const query = event.target.value.toLowerCase();
    setSearchQuery(query);

    if (query) {
      const filtered = calculatedRecords.filter(
        (record) =>
          record?.collection_name?.toLowerCase().includes(query) ||
          record?.document_name?.toLowerCase().includes(query)
      );
      setFilteredRecords(filtered);
    } else {
      setFilteredRecords(calculatedRecords);
    }
  };

  const handleCollection = ({ e, collectionId }) => {
    if (e) e.stopPropagation();
    const array = [...expandedCollections];
    if (expandedCollections?.has(collectionId)) {
      setExpandedCollections(new Set(array?.filter((el) => el !== collectionId)));
    } else {
      setExpandedCollections(new Set([...expandedCollections, collectionId]));
    }
  };

  // Validation function with error messages using message.error
  const validation = ({ values, noLeft }) => {
    const validTypes = ['doc', 'docx', 'dot', 'dotx', 'docm', 'dotm', 'pdf'];
    const maxFiles = noLeft || DOCCHAT_MAX_FILES;

    // Check if files exist
    if (!values?.file || values.file.length === 0) {
      return { file: 'No file selected' };
    }

    const fileArr = Array.from(values.file);

    // Validate file count
    if (fileArr.length > maxFiles) {
      return { file: `Upload up to ${maxFiles} documents` };
    }

    // Validate file types
    const invalidFiles = fileArr.filter(
      (file) => !validTypes.includes(file.name.split('.').pop()?.toLowerCase())
    );

    if (invalidFiles.length > 0) {
      return { file: 'Invalid file type(s) detected' };
    }

    return {};
  };

  const handleClearChat = (data) => {
    // trackButtonClick({ type: 'Clear Chat' });
    dispatch(
      deleteChat({
        collection_id: data?.collection_id,
        documentId: data?.document_id,
      })
    );
  };

  const handleMoveToFolderFunc = (value) => {
    handleMoveToFolder({
      collectionName: value,
      documentId: modalData.data?.document_id,
      setLoader,
      closeModal,
      userId: userData?.id,
      dispatch,
      pathname,
      navigate,
    });
  };

  const onClick = () => {
    if (!sidePanelVisible) {
      setSidePanelVisible(true);
    }
  };
  return (
    <>
      <section
        className={`saved_data_docchat ${sidePanelVisible ? '' : 'collapse-active'} ${screen === 'docchat-dashboard' ? '' : 'hide-box-shadow'}`}
        id='saved_data'
      >
        <div className='saved_data-inner' id='saved_data-tour-step-2'>
          <div
            // style={{ padding: '1rem' }}
            className='saved-data-search-container'
            onClick={onClick}
          >
            <Input
              className='saved-docchat-search-input'
              prefix={<SearchOutlined />}
              placeholder='Search Files'
              style={{ height: '40px' }}
              onChange={handleSearch}
              value={searchQuery}
            />
          </div>
          <button className='saved_data-collapse-btn' onClick={handleSidePanel}>
            <CollapseSidePanelIcon />
          </button>
          {pathname !== '/docchat' && (
            <div className='saved_data-collapse-section mb-16'>
              <div className='form-wrapper' style={{ flex: 1 }}>
                {docChatQuota === 0 ? (
                  <Tooltip placement='right' title='You have exhausted your quota.'>
                    <Button
                      type='primary'
                      disabled
                      icon={<FileUploadOutlined fill='var(--grey-6)' />}
                    >
                      {sidePanelVisible ? 'Upload Document' : ''}
                    </Button>
                  </Tooltip>
                ) : (
                  <Formik
                    initialValues={{ file: null }}
                    validate={(values) => validation({ values })}
                  >
                    {({ setFieldValue }) => (
                      <Form className='form'>
                        <input
                          type='file'
                          name='file'
                          onChange={(event) => {
                            const files = Array.from(event.target.files);
                            setFieldValue('file', files);

                            // Clear previous validation error if new files are selected
                            const validationErrors = validation({
                              values: { file: files },
                            });

                            // Only trigger the upload if no validation errors exist
                            if (Object.keys(validationErrors).length === 0) {
                              handleUpload({ file: files });
                            }

                            // If there are errors, don't proceed with upload
                            if (Object.keys(validationErrors).length > 0) {
                              setFieldValue('file', '');
                              message.error(validationErrors?.file);
                            }
                          }}
                          hidden
                          ref={fileRef}
                          multiple
                          accept={acceptTypes}
                        />
                        <div className='upload-doc-btn-container'>
                          <Button
                            type='primary'
                            style={{ height: '42px', width: '100%' }}
                            onClick={() => {
                              // Trigger the file input click
                              fileRef.current.click();
                            }}
                            icon={<FileUploadOutlined />}
                          >
                            {sidePanelVisible ? 'Upload Document' : ''}
                          </Button>
                        </div>
                      </Form>
                    )}
                  </Formik>
                )}
              </div>
            </div>
          )}
          {savedDocChatRecordStatus === LOADING && <SavedComparisonSkeleton />}
          <ul
            className={`saved-document-container ${pathname === '/docchat' ? 'expand' : ''}`}
            ref={sidePanelRef}
          >
            {calculatedFilteredRecords?.length > 0 &&
              calculatedFilteredRecords?.map((item, i) => (
                <SavedComparisonsListItem
                  key={item.chat_id}
                  index={i}
                  data={item}
                  selectedCollection={selectedCollection}
                  selectedCollectionRef={selectedCollectionRef}
                  selectedDoc={selectedDoc}
                  uploadStatus={uploadStatus}
                  handleDocumentClick={handleDocumentClick}
                  expandedCollections={expandedCollections}
                  handleCollection={handleCollection}
                  addRef={addRef}
                  validation={validation}
                  handleModal={handleModal}
                  handleUpload={handleUpload}
                  records={calculatedRecords}
                  handleClearChat={handleClearChat}
                  position={position}
                  uuid={uuid}
                />
              ))}
          </ul>
          {savedDocChatRecordStatus === ERROR && (
            <p className='fs-14 text-center color-danger hide-on-collapse'>Something went wrong!</p>
          )}
          {savedDocChatRecordStatus === SUCCESS && calculatedRecords?.length === 0 && (
            <p className='fs-14 text-center hide-on-collapse'>You have 0 conversations.</p>
          )}
        </div>
      </section>
      <UpdateFileNameModal
        isOpen={modalData.edit}
        initialText={
          modalData.data?.collection_id
            ? modalData.data?.collection_name
            : modalData.data?.document_name
        }
        onClose={closeModal}
        onSave={handleEditDocName}
        loading={loader === 'EDIT'}
        extraValidation={editValidation}
      />
      <ConfirmationModal
        isOpen={modalData.delete}
        text={`Are you sure you want to delete ${modalData.data?.collection_id ? 'folder' : 'file'} "${modalData.data?.[modalData.data?.collection_id ? 'collection_name' : 'document_name']}"?`}
        onCancel={closeModal}
        onSubmit={handleDeleteDoc}
        loading={loader === 'DELETE'}
        submitButtonProps={{ danger: true }}
      />
      <CreateFolderModal
        isOpen={modalData.newFolder}
        onClose={closeModal}
        onSave={handleMoveToFolderFunc}
        loading={loader === 'MOVE_TO_NEW_FOLDER'}
      />
    </>
  );
}

export default SavedComparisonsDocChat;
